import React, { ReactNode, FunctionComponent, useState, useEffect } from 'react';

import classnames from 'classnames';
import _ from 'lodash';
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { FeatureFlagId } from 'admin/feature-flags/FeatureFlags';
import { isAuthorized } from 'auth/Auth';
import { ErrorDisplay } from 'social/publisher/error-display/ErrorDisplay';
import {
    LocationSource,
    PublisherError,
    SelectedSource,
    SourceName,
    EnabledSource,
} from 'social/publisher/Publisher.types';
import { usePublisherDispatch } from 'social/publisher/PublisherContext';
import { getSourcesAvailable } from 'social/publisher/util/Util';

import 'social/publisher/source-picker/SourcePicker.scss';

interface Props {
    locationSources: LocationSource[];
    selectedSources: SelectedSource;
    publisherErrors: PublisherError[];
    isSourcesLocked: boolean;
    enabledSources: EnabledSource;
    isLibraryContentCreateMode: boolean;
    isCampaignCreateMode: boolean;
}

export const SourcePicker: FunctionComponent<Props> = ({
    locationSources,
    selectedSources,
    publisherErrors,
    isSourcesLocked,
    enabledSources,
    isLibraryContentCreateMode,
    isCampaignCreateMode,
}) => {
    const dispatch = usePublisherDispatch();
    const { t } = useTranslation(['social/publisher']);
    const [isModalShowing, setIsModalShowing] = useState<boolean>(false);
    const [sourceInModal, setSourceInModal] = useState<SourceName>('GOOGLE_PLACES');
    const [modalMessage, setModalMessage] = useState<string>(
        t('SOURCE_PICKER_MODAL_MESSAGE', { sourceName: t(sourceInModal) }),
    );
    const picker: ReactNode[] = [];
    const nonApiSourcesPicker: ReactNode[] = [];
    const sourcesAvailable = getSourcesAvailable(enabledSources, locationSources);
    const SOURCE_TOOLTIPS: { [index: string]: string | null } = {
        FACEBOOK: null,
        TWITTER: isLibraryContentCreateMode ? null : t('TWITTER_ONLY_ONE_LOCATION'),
        INSTAGRAM: t('IG_POST'),
        IG_REELS: t('IG_REELS'),
        GOOGLE_PLACES: isLibraryContentCreateMode ? null : t('GMB_CANNOT_BE_SELECTED_WITH_OTHER_SOURCES'),
        LINKEDIN: isLibraryContentCreateMode ? null : t('LINKEDIN_ONLY_ONE_LOCATION'),
        YOUTUBE: isLibraryContentCreateMode ? null : t('YOUTUBE_CANNOT_BE_SELECTED_WITH_OTHER_SOURCES'),
        IG_STORIES: t('IG_STORIES'),
        TIKTOK: null,
    };
    const NON_API_SOURCES: SourceName[] = ['IG_STORIES'];
    // sources that can not be selected with other sources
    const LONE_SOURCES: SourceName[] = isLibraryContentCreateMode
        ? ['IG_STORIES']
        : ['GOOGLE_PLACES', 'YOUTUBE', 'IG_STORIES'];
    const selectSource = (key: SourceName, selection: boolean): void => {
        const newSources = { ...selectedSources };

        if (key === 'FACEBOOK' || key === 'INSTAGRAM' || key === 'TIKTOK' || key === 'IG_REELS') {
            if (selection) {
                let loneSourceSelected = false;

                Object.keys(selectedSources).forEach((key) => {
                    if (LONE_SOURCES.includes(key as SourceName) && selectedSources[key]) {
                        loneSourceSelected = true;
                    }
                });

                if (loneSourceSelected) {
                    setSourceInModal(key);
                    setModalMessage(t('NON_API_SOURCE_PICKER_MODAL_MESSAGE'));
                    openModal();
                } else {
                    newSources[key] = true;
                    LONE_SOURCES.forEach((el) => {
                        newSources[el] = false;
                    });

                    if (key === 'IG_REELS') {
                        newSources.INSTAGRAM = false;
                    } else if (key === 'INSTAGRAM') {
                        newSources.IG_REELS = false;
                    }
                }
            } else {
                newSources[key] = false;
            }
        }

        if (key === 'TWITTER' || key === 'LINKEDIN') {
            if (selection) {
                let loneSourceSelected = false;

                Object.keys(selectedSources).forEach((key) => {
                    if (LONE_SOURCES.includes(key as SourceName) && selectedSources[key]) {
                        loneSourceSelected = true;
                    }
                });

                if (loneSourceSelected) {
                    setSourceInModal(key);
                    setModalMessage(t('NON_API_SOURCE_PICKER_MODAL_MESSAGE'));
                    openModal();
                } else {
                    newSources[key] = true;
                    LONE_SOURCES.forEach((el) => {
                        newSources[el] = false;
                    });
                }
            } else {
                newSources[key] = false;
            }
        }

        // GOOGLE/YOUTUBE can not be selected with other sources, we open a modal to ask user to either confirm or cancel the switch before selecting GOOGLE/YOUTUBE/IG_REELS and deselecting other sources
        if (key === 'GOOGLE_PLACES' || key === 'YOUTUBE') {
            if (selection) {
                if (isLibraryContentCreateMode) {
                    newSources[key] = true;
                } else {
                    const isAnyOtherSourceSelected = _.values(selectedSources).findIndex((el) => el === true) > -1;

                    if (isAnyOtherSourceSelected) {
                        setSourceInModal(key);
                        setModalMessage(t('SOURCE_PICKER_MODAL_MESSAGE', { sourceName: t(key) }));
                        openModal();
                    } else {
                        _.forEach(newSources, (v, k) => {
                            newSources[k] = false;
                        });
                        newSources[key] = true;
                    }
                }
            } else {
                newSources[key] = false;
            }
        }

        // Only 1 non-API source can be selected for the whole SP
        if (NON_API_SOURCES.includes(key)) {
            if (selection) {
                const isAnyOtherSourceSelected = _.values(selectedSources).findIndex((el) => el === true) > -1;

                if (isAnyOtherSourceSelected) {
                    setSourceInModal(key);
                    setModalMessage(t('SOURCE_PICKER_MODAL_MESSAGE', { sourceName: t(key) }));
                    openModal();
                } else {
                    _.forEach(newSources, (v, k) => {
                        newSources[k] = false;
                    });
                    newSources[key] = true;
                }
            } else {
                newSources[key] = false;
            }
        }

        dispatch({ type: 'set-selected-sources', payload: { value: newSources } });
    };
    const handleSelectSource = (sourceName: SourceName): void => {
        if (selectedSources[sourceName]) {
            selectSource(sourceName, false);
        } else {
            selectSource(sourceName, true);
        }

        // Resets the Source errors
        dispatch({ type: 'remove-publisher-errors-by-component', payload: { value: 'SOURCE' } });
        // Resets the Location errors
        dispatch({ type: 'remove-publisher-errors-by-component', payload: { value: 'LOCATION' } });
    };
    const openModal = (): void => {
        setIsModalShowing(true);
    };
    const handleModalCancel = (): void => {
        setIsModalShowing(false);
    };
    const handleModalConfirm = (): void => {
        const newSources = { ...selectedSources };

        _.forEach(newSources, (v, k) => {
            newSources[k] = false;
        });
        newSources[sourceInModal] = true;
        dispatch({ type: 'set-selected-sources', payload: { value: newSources } });
        setIsModalShowing(false);
    };
    const isSingleSourceUser = _.values(sourcesAvailable).filter((isSourceAvailable) => isSourceAvailable).length === 1;
    let singleSource = '';
    let totalSources = 0;

    _.forEach(sourcesAvailable, (isSourceAvailable, sourceName): void => {
        const socialIconCSS = classnames('social-icon', {
            selectable: isSourceAvailable && !selectedSources[sourceName],
            unselectable: !isSourceAvailable,
            active: selectedSources[sourceName],
            locked: isSourcesLocked || isSingleSourceUser,
        });

        if (selectedSources[sourceName]) {
            totalSources += 1;
        }

        if (isSourceAvailable) {
            let sourceEl;

            if (isSingleSourceUser) {
                singleSource = sourceName;
            }
            if (SOURCE_TOOLTIPS[sourceName]) {
                sourceEl = (
                    <OverlayTrigger
                        key={sourceName}
                        placement="top-end"
                        overlay={
                            <Tooltip id={`tooltip-${sourceName.toLowerCase()}`}>{SOURCE_TOOLTIPS[sourceName]}</Tooltip>
                        }
                    >
                        <Button
                            className={`${socialIconCSS} ${sourceName.toLowerCase()}`}
                            key={sourceName}
                            name={sourceName}
                            onClick={(): void => {
                                if (isSourcesLocked || isSingleSourceUser) {
                                    return;
                                }
                                handleSelectSource(sourceName as SourceName);
                            }}
                            variant="action"
                        >
                            {!NON_API_SOURCES.includes(sourceName as SourceName) &&
                                sourceName !== 'INSTAGRAM' &&
                                sourceName !== 'IG_REELS' && <i className="icon-info-circle" />}
                            <span>{t(sourceName)}</span>
                        </Button>
                    </OverlayTrigger>
                );
            } else {
                sourceEl = (
                    <Button
                        className={`${socialIconCSS} ${sourceName.toLowerCase()}`}
                        key={sourceName}
                        name={sourceName}
                        onClick={(): void => {
                            if (isSourcesLocked || isSingleSourceUser) {
                                return;
                            }
                            handleSelectSource(sourceName as SourceName);
                        }}
                        variant="action"
                    >
                        <span>{t(sourceName)}</span>
                    </Button>
                );
            }

            if (NON_API_SOURCES.includes(sourceName as SourceName)) {
                nonApiSourcesPicker.push(sourceEl);
            } else {
                picker.push(sourceEl);
            }
        }
    });

    useEffect(() => {
        if (isSingleSourceUser && singleSource) {
            const newSources: SelectedSource = {
                FACEBOOK: false,
                INSTAGRAM: false,
                TWITTER: false,
                GOOGLE_PLACES: false,
                LINKEDIN: false,
                YOUTUBE: false,
                IG_STORIES: false,
                IG_REELS: false,
                TIKTOK: false,
            };

            newSources[singleSource] = true;
            dispatch({ type: 'set-selected-sources', payload: { value: newSources } });
        }
    }, [isSingleSourceUser, singleSource, dispatch]);

    return (
        <div className="r4e-social-source-picker">
            <div className="source-picker-header">
                <div className="source-picker-title-container">
                    <h3 className="source-picker-title">
                        {t('PUBLISH_TO')} <span>({t('COUNT_SELECTED', { count: totalSources })})</span>
                    </h3>
                    {picker}
                </div>
                {/* some tenants don't want to see publishing assistant */}
                {!isAuthorized({ featureFlags: [FeatureFlagId.SocialHidePublishAssistant] }) &&
                    nonApiSourcesPicker.length > 0 &&
                    !isCampaignCreateMode && (
                        <div className="source-picker-title-container">
                            <h3 className="source-picker-title">
                                {t('PUBLISH_ASSISTANT')}{' '}
                                <OverlayTrigger
                                    key="PUBLISH_ASSISTANT"
                                    placement="top-end"
                                    overlay={
                                        <Tooltip id="publish-assistant-tooltip">
                                            {t('CANNOT_BE_SELECTED_WITH_OTHER_SOURCES', { count: totalSources })}
                                        </Tooltip>
                                    }
                                >
                                    <i className="icon-info-circle" />
                                </OverlayTrigger>
                            </h3>
                            {nonApiSourcesPicker}
                        </div>
                    )}
            </div>
            <ErrorDisplay publisherErrors={publisherErrors} component="SOURCE" />
            <Modal show={isModalShowing} onHide={handleModalCancel} className="r4e-source-picker-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{t('SOURCE_PICKER_MODAL_HEADER')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modalMessage}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalCancel}>
                        {t('CANCEL')}
                    </Button>
                    <Button variant="primary" onClick={handleModalConfirm}>
                        {t('CONTINUE')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
